import type { MapBounds, ReviewLocationSummary } from "../api";

const TTL_MS = 5 * 60 * 1000;
const MAX_MEMORY_ENTRIES = 48;
const SESSION_PREFIX = "rmc_map_bounds_v1:";
const MAX_SESSION_KEYS = 24;

type CacheEntry = {
  locations: ReviewLocationSummary[];
  expiresAt: number;
};

/** LRU: most-recently-used at end of Map iteration */
const memory = new Map<string, CacheEntry>();

function roundKey(n: number, decimals: number): string {
  const f = 10 ** decimals;
  return String(Math.round(n * f) / f);
}

/** Stable key so tiny float jitter from the map still hits the same bucket (~11 m at 4 decimals). */
export function boundsCacheKey(bounds: MapBounds): string {
  const d = 4;
  return [
    roundKey(bounds.south, d),
    roundKey(bounds.north, d),
    roundKey(bounds.west, d),
    roundKey(bounds.east, d),
  ].join("|");
}

function isFresh(entry: CacheEntry): boolean {
  return Date.now() < entry.expiresAt;
}

function touchMemory(key: string, entry: CacheEntry): void {
  memory.delete(key);
  memory.set(key, entry);
  while (memory.size > MAX_MEMORY_ENTRIES) {
    const first = memory.keys().next().value;
    if (first === undefined) break;
    memory.delete(first);
  }
}

function readSession(key: string): CacheEntry | null {
  try {
    const raw = sessionStorage.getItem(SESSION_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEntry;
    if (!parsed || !Array.isArray(parsed.locations) || typeof parsed.expiresAt !== "number") {
      return null;
    }
    if (!isFresh(parsed)) {
      sessionStorage.removeItem(SESSION_PREFIX + key);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeSession(key: string, entry: CacheEntry): void {
  try {
    const payload = JSON.stringify(entry);
    if (payload.length > 450_000) return;
    sessionStorage.setItem(SESSION_PREFIX + key, payload);
    pruneSessionKeys();
  } catch {
    try {
      sessionStorage.removeItem(SESSION_PREFIX + key);
    } catch {
      /* ignore */
    }
  }
}

function pruneSessionKeys(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k?.startsWith(SESSION_PREFIX)) keys.push(k);
    }
    if (keys.length <= MAX_SESSION_KEYS) return;
    keys.sort();
    for (let j = 0; j < keys.length - MAX_SESSION_KEYS; j++) {
      sessionStorage.removeItem(keys[j]!);
    }
  } catch {
    /* ignore */
  }
}

/** Locations inside the query rectangle (handles cache buckets slightly larger than the live view). */
export function filterLocationsToBounds(
  locations: ReviewLocationSummary[],
  bounds: MapBounds
): ReviewLocationSummary[] {
  const { south, north, west, east } = bounds;
  return locations.filter((loc) => {
    const lat = Number(loc.latitude);
    const lng = Number(loc.longitude);
    return lat >= south && lat <= north && lng >= west && lng <= east;
  });
}

export function getMapBoundsCached(bounds: MapBounds): ReviewLocationSummary[] | null {
  const key = boundsCacheKey(bounds);
  const mem = memory.get(key);
  if (mem && isFresh(mem)) {
    touchMemory(key, mem);
    return filterLocationsToBounds(mem.locations, bounds);
  }
  if (mem && !isFresh(mem)) memory.delete(key);

  const fromSession = readSession(key);
  if (fromSession) {
    touchMemory(key, fromSession);
    return filterLocationsToBounds(fromSession.locations, bounds);
  }
  return null;
}

export function setMapBoundsCached(
  bounds: MapBounds,
  locations: ReviewLocationSummary[]
): void {
  const key = boundsCacheKey(bounds);
  const entry: CacheEntry = {
    locations,
    expiresAt: Date.now() + TTL_MS,
  };
  touchMemory(key, entry);
  writeSession(key, entry);
}
